/**
 * dataExport.js — P5 D4 (PROD-171): the patient exports her own record, in the app.
 *
 * ★★★ ALEX P5 D4. "The patient starts the export in the app and downloads the zip through
 *   a short-lived link. No admin handles the data." The API side is P5 step 8 (orella-api
 *   ad86f62, in 568759a), two routes that answer only a patient acting for herself:
 *
 *     POST /api/v1/data-export/generate
 *       201 {request_id, complete, files, failed, download_url | null, expires_in | null}
 *       413 export_too_large | export_timeout (nothing was stored)
 *       403 export_patient_only, 500/503 export_unavailable | export_upload_failed |
 *       export_record_failed
 *     GET  /api/v1/data-export/<request_id>/download
 *       200 {request_id, download_url, expires_in: 600}
 *       404 export_not_found | export_not_ready, 500 export_unavailable | export_link_failed
 *
 *   The link lasts 600 s. `download_url` can be null on a 201 when only the signing
 *   failed; the zip is stored, so this asks the download route for a link instead of
 *   calling the export a failure.
 *
 * ★★ AN INCOMPLETE EXPORT IS NEVER REPORTED AS COMPLETE. When a stored object could not
 *   be fetched the server still builds the zip, lists it in manifest.missing and answers
 *   complete:false with `failed` = the count. That is 'incomplete' here, with the count,
 *   so the screen can say how many files are not in the zip.
 *
 * ★★ THE LINK IS OPENED ONLY WHEN IT IS https. Anything else (a missing value, another
 *   scheme) is a failure, never a navigation.
 *
 * ★ DELIVERY. The file is fetched and handed to saveOrShareBlob (a web download, or the
 *   native share sheet with "Save to Files", which presents OVER the app). If the fetch
 *   fails (a network drop, or a bucket that does not allow this origin), the link itself
 *   is opened: native through @capacitor/browser (over the app, the SPA stays mounted),
 *   web through a plain link click (a zip response downloads and the page stays).
 *
 * ★ Every function takes its dependencies as arguments, and the module has no static
 *   imports, so scripts/check-data-export-entry.mjs runs it in node as is. Nothing here
 *   renders text: the screen maps the keys below through t().
 */

export const EXPORT_LINK_SECONDS = 600

/** The translation key for a thrown apiFetch value from either export route. */
export function exportErrorKey(err) {
  const code = err && err.code
  const status = err && err.status
  if (code === 'export_too_large') return 'dataExport.tooLarge'
  if (code === 'export_timeout') return 'dataExport.timeout'
  if (code === 'export_patient_only') return 'dataExport.patientOnly'
  if (code === 'export_not_found' || code === 'export_not_ready') return 'dataExport.notFound'
  if (status === 0 || status === 408) return 'dataExport.network'
  return 'dataExport.failed'
}

/** Only an https URL is ever handed to a browser or a fetch. */
export function isDeliverableLink(url) {
  return typeof url === 'string' && /^https:\/\/[^\s/]+\/\S*$/i.test(url.trim())
}

/** orella-my-record-YYYY-MM-DD.zip, on the device's local date. */
export function exportFileName(today = new Date()) {
  const p = (n) => String(n).padStart(2, '0')
  return `orella-my-record-${today.getFullYear()}-${p(today.getMonth() + 1)}-${p(today.getDate())}.zip`
}

const count = (v) => (Number.isInteger(v) && v >= 0 ? v : null)
const failure = (key, extra = {}) => ({ kind: 'error', key, ...extra })

async function linkFor(api, requestId) {
  try {
    const d = await api.download(requestId)
    return isDeliverableLink(d && d.download_url) ? { url: d.download_url.trim() } : { key: 'dataExport.failed' }
  } catch (err) {
    return { key: exportErrorKey(err), status: err && typeof err.status === 'number' ? err.status : null }
  }
}

/**
 * Start the export and deliver the zip.
 * @param {{generate: Function, download: Function}} api   dataExportAPI
 * @param {(url: string) => Promise<string>} deliver        saves or opens the link
 * @returns {Promise<{kind: 'ready'|'incomplete'|'error', key?, status?, requestId, files, failed, method?}>}
 */
export async function runMyDataExport({ api, deliver }) {
  let gen
  try {
    gen = await api.generate()
  } catch (err) {
    return failure(exportErrorKey(err), { status: err && typeof err.status === 'number' ? err.status : null, requestId: null, files: null, failed: null })
  }
  const requestId = gen && typeof gen.request_id === 'string' && gen.request_id ? gen.request_id : null
  const files = count(gen && gen.files)
  const failed = count(gen && gen.failed)
  const complete = !!gen && gen.complete === true && failed === 0
  const meta = { requestId, files, failed }

  let url = isDeliverableLink(gen && gen.download_url) ? gen.download_url.trim() : null
  if (!url) {
    if (!requestId) return failure('dataExport.failed', { status: null, ...meta })
    const l = await linkFor(api, requestId)
    if (!l.url) return failure(l.key, { status: l.status ?? null, ...meta })
    url = l.url
  }
  let method
  try {
    method = await deliver(url)
  } catch (_) {
    return failure('dataExport.saveFailed', { status: null, ...meta })
  }
  return { kind: complete ? 'ready' : 'incomplete', ...meta, method }
}

/** A fresh 600 s link to an export already built, delivered again. The caller keeps the
 *  complete/incomplete verdict of the first answer: this route does not repeat it. */
export async function redownloadMyDataExport({ api, deliver, requestId, files = null, failed = null }) {
  const meta = { requestId: requestId || null, files: count(files), failed: count(failed) }
  if (!requestId) return failure('dataExport.notFound', { status: null, ...meta })
  const l = await linkFor(api, requestId)
  if (!l.url) return failure(l.key, { status: l.status ?? null, ...meta })
  let method
  try {
    method = await deliver(l.url)
  } catch (_) {
    return failure('dataExport.saveFailed', { status: null, ...meta })
  }
  return { kind: 'delivered', ...meta, method }
}

/**
 * Fetch the zip and save it; if the fetch fails, open the link instead.
 * @returns {Promise<'saved'|'opened'>}
 */
export async function deliverExportLink(url, { fetchImpl, save, open, filename }) {
  if (!isDeliverableLink(url)) throw new Error('not an https link')
  let blob = null
  try {
    const r = await fetchImpl(url)
    if (r && r.ok) blob = await r.blob()
  } catch (_) {
    blob = null
  }
  if (blob) {
    await save(blob, filename)
    return 'saved'
  }
  await open(url)
  return 'opened'
}

/** The device's way to open a link without leaving the app (native) or the page (web). */
export async function openLinkOnDevice(url) {
  if (!isDeliverableLink(url)) throw new Error('not an https link')
  const { Capacitor } = await import('@capacitor/core')
  if (Capacitor.isNativePlatform()) {
    const { Browser } = await import('@capacitor/browser')
    await Browser.open({ url, presentationStyle: 'popover' })
    return
  }
  const a = document.createElement('a')
  a.href = url
  a.rel = 'noopener noreferrer'
  document.body.appendChild(a)
  a.click()
  a.remove()
}
